"use client";

import { memo, useEffect, useRef, useState } from "react";
import { ACCENT, Bot, Bubble, Desk, ENGINES, GUIDE_LOOK, GuideCarry, MONO, TXT, Plant, QUESTIONS, Rep, Tag, along, pathLen, useScrollTime } from "@/app/home/office/parts";
import type { Look, Pt } from "@/app/home/office/parts";

// A non-interactive, top-down "agent office". Scroll drives one pass of the story:
// the office pins, the page guide walks to each agent in turn, and one page travels
// from the AI-engine wall through every agent to an enquiry on your website.
// Only the active agent is bright and speaks, so the eye has one place to go.

const VB_W = 1200;
const VB_H = 680;

const STICKY_TOP = 76; // px below the top of the viewport where the office pins
const RUNWAY = "420vh"; // scroll distance while the office is pinned

const SY1 = 262; // seat line, top row
const SY2 = 467; // seat line, bottom row



type Walker = { name: string; seat: Pt; route: Pt[]; next: string; verb: string; look: Look };

// Serpentine flow: top row left→right, bottom row right→left, then out to your website.
export const WALKERS: Walker[] = [
  { name: "Insight", seat: [170, SY1], route: [[310, SY1], [355, SY1]], next: "Intent Agent", verb: "mapping AI gaps", look: { shirt: "#14B8A6", pants: "#334155", skin: "#F6C9A0", hair: "#3B2A20", style: "side", acc: "glasses" } },
  { name: "Intent", seat: [450, SY1], route: [[635, SY1]], next: "Instruction Agent", verb: "picking intents", look: { shirt: "#7C5CE0", pants: "#2F333D", skin: "#E0A47A", hair: "#1F1A17", style: "spiky", acc: "headset" } },
  { name: "Instruction", seat: [730, SY1], route: [[915, SY1]], next: "Keyword Agent", verb: "writing brief", look: { shirt: "#22A06B", pants: "#3B3F4A", skin: "#F3D2B3", hair: "#8B4A2B", style: "bun", acc: "pencil" } },
  { name: "Keyword", seat: [1010, SY1], route: [[1105, SY1], [1105, SY2]], next: "Content Agent", verb: "adding keywords", look: { shirt: ACCENT, pants: "#2F333D", skin: "#C98B62", hair: "#2A2A2A", style: "beanie" } },
  { name: "Content", seat: [1010, SY2], route: [[825, SY2]], next: "Meta & SEO Agent", verb: "writing page", look: { shirt: "#E8626F", pants: "#3B3F4A", skin: "#F6C9A0", hair: "#5A3825", style: "long", acc: "beret" } },
  { name: "Meta & SEO", seat: [730, SY2], route: [[545, SY2]], next: "Assembler", verb: "adding schema", look: { shirt: "#3B3F4A", pants: "#25272D", skin: "#D9A27C", hair: "#111111", style: "cap" } },
  { name: "QA Gate", seat: [170, SY2], route: [[60, SY2], [60, 556], [605, 556], [605, 628]], next: "you for approval", verb: "scoring", look: { shirt: "#F2A900", pants: "#334155", skin: "#B97A55", hair: "#2B1D14", style: "curly", acc: "badge" } },
];
const BOT_X = 450;


const HUMAN: Pt = [560, 628];
export const HUMAN_LOOK: Look = { shirt: "#FFFFFF", pants: "#25272D", skin: "#E7B08A", hair: "#3A2A1E", style: "side", acc: "badge" };

// Stationary agents: they never walk, they work at their own desks.
const SY0 = 140; // seat line, desks by the AI-search wall
const SYB = 605; // seat line, desks below the bottom row
type Station = { name: string; seat: Pt; look: Look };
export const EXEC: Station = { name: "Execution Agent", seat: [640, SY0], look: { shirt: "#0EA5E9", pants: "#334155", skin: "#EBB98F", hair: "#1F1A17", style: "curly", acc: "headset", accColor: ACCENT } };
export const MONITOR: Station = { name: "Monitor Agent", seat: [820, SY0], look: { shirt: "#F472B6", pants: "#2F333D", skin: "#C98B62", hair: "#2A1A12", style: "long", acc: "clipboard" } };
export const SITE_AUDIT: Station = { name: "Site Audit Agent", seat: [790, SYB], look: { shirt: "#B45309", pants: "#3B3F4A", skin: "#F3D2B3", hair: "#3B2A20", style: "side", acc: "hardhat" } };
export const BRAND: Station = { name: "Brand Agent", seat: [1040, SYB], look: { shirt: "#84CC16", pants: "#334155", skin: "#D9A27C", hair: "#5A3825", style: "bun", acc: "palette" } };
const STATIONS = [EXEC, MONITOR, SITE_AUDIT, BRAND];
const LEAD: Pt = [420, 628]; // stands between your website and you
export const LEAD_LOOK: Look = { shirt: "#DC2626", pants: "#25272D", skin: "#E0A47A", hair: "#111111", style: "spiky", acc: "phone" };

export const MONITOR_LINES = ["brand mentioned · ChatGPT", "competitor cited · 3 engines", "new citation · 4 sources", "GEO score · 72 → 74"];
export const BRAND_LINES = ["reading your logo", "matching your colors", "brand kit ready"];
export const AUDIT_LINES = ["crawling yourdomain.com", "AI crawler visit · GPTBot", "schema check · 2 fixes"];


const Room = memo(function Room() {
  return (
    <g>
      <defs>
        <pattern id="af-tile" width="40" height="40" patternUnits="userSpaceOnUse">
          <rect width="40" height="40" fill="var(--il-floor)" />
          <path d="M40 0H0V40" fill="none" stroke="var(--il-floor-line)" strokeWidth="1" />
        </pattern>
      </defs>
      <rect x={20} y={20} width={1160} height={640} rx={14} fill="url(#af-tile)" />
      {/* walls */}
      <rect x={20} y={20} width={1160} height={30} rx={14} fill="var(--il-wall)" />
      <rect x={20} y={44} width={1160} height={6} fill="var(--il-wall-edge)" />
      <rect x={20} y={20} width={1160} height={640} rx={14} fill="none" stroke="var(--il-wall-edge)" strokeWidth={8} />

      {/* rugs under the work rows */}
      <rect x={70} y={196} width={1060} height={136} rx={10} fill="var(--il-rug)" />
      <rect x={70} y={400} width={1060} height={136} rx={10} fill="var(--il-rug)" />

      {/* AI search wall */}
      {ENGINES.map((e, i) => (
        <g key={e.name}>
          <rect x={58 + i * 82} y={34} width={70} height={48} rx={5} fill="#2F333D" />
          <rect x={62 + i * 82} y={38} width={62} height={34} rx={3} fill={e.color} opacity={0.9} />
          <text x={93 + i * 82} y={59} textAnchor="middle" fontFamily={MONO} fontSize={8.5} fontWeight={700} fill="#FFFFFF">
            {e.name}
          </text>
        </g>
      ))}
      <text x={305} y={102} textAnchor="middle" fontFamily={MONO} fontSize={10} fontWeight={700} letterSpacing={2} fill="rgba(37,39,45,0.45)">
        AI SEARCH
      </text>

      {/* bookshelf + wall clock */}
      <rect x={930} y={30} width={130} height={52} rx={4} fill="#D9C2A0" />
      {[0, 1].map((row) =>
        [0, 1, 2, 3, 4, 5, 6, 7].map((c) => (
          <rect key={`${row}-${c}`} x={938 + c * 15} y={36 + row * 23} width={10} height={18} rx={1.5} fill={["#2E51D6", "#F2A900", "#10A37F", "#E8626F", "#7C5CE0"][(c + row * 3) % 5]} />
        )),
      )}

      {/* your website terminal */}
      <rect x={130} y={566} width={250} height={80} rx={8} fill="#2F333D" />
      <rect x={136} y={572} width={238} height={68} rx={5} fill="var(--il-panel)" />
      <rect x={136} y={572} width={238} height={12} rx={5} fill={ACCENT} />
      <circle cx={144} cy={578} r={2} fill="#FFFFFF" />
      <circle cx={151} cy={578} r={2} fill="#FFFFFF" opacity={0.7} />
      <text x={255} y={580.5} textAnchor="middle" fontFamily={MONO} fontSize={7.5} fontWeight={700} fill="#FFFFFF">
        yourdomain.com
      </text>
      <text x={255} y={659} textAnchor="middle" fontFamily={MONO} fontSize={9} fontWeight={700} letterSpacing={2} fill="rgba(37,39,45,0.45)">
        YOUR WEBSITE
      </text>

      {/* conveyor from the assembler to QA */}
      <rect x={246} y={SY2 - 9} width={128} height={14} rx={4} fill="#475569" />
      {[258, 278, 298, 318, 338, 358].map((bx) => (
        <line key={bx} x1={bx} y1={SY2 - 7} x2={bx} y2={SY2 + 3} stroke="#64748B" strokeWidth={2} />
      ))}

      <Plant x={46} y={180} />
      <Plant x={1150} y={180} s={1.1} />
      <Plant x={1160} y={650} s={0.9} />
      <Plant x={1080} y={110} s={0.9} />
    </g>
  );
});



/* ---------- the story: one step per agent, in the order the work happens ---------- */

// `spot` is where the guide stands while it explains that agent.
type Step = { id: string; name: string; what: string; spot: Pt };
const STEPS: Step[] = [
  { id: "exec", name: "Execution Agent", what: "asks your buyers' questions on every AI engine", spot: [540, SY0 + 30] },
  { id: "monitor", name: "Monitor Agent", what: "logs who gets mentioned, cited and ranked", spot: [920, SY0 + 30] },
  { id: "audit", name: "Site Audit Agent", what: "crawls your site and finds what to fix", spot: [890, SYB + 30] },
  { id: "brand", name: "Brand Agent", what: "learns your logo, colours and voice", spot: [940, SYB + 30] },
  { id: "w0", name: "Insight Agent", what: "turns the gaps into a priority map", spot: [270, SY1 + 30] },
  { id: "w1", name: "Intent Agent", what: "picks the buyer questions worth a page", spot: [550, SY1 + 30] },
  { id: "w2", name: "Instruction Agent", what: "writes the brief for the page", spot: [830, SY1 + 30] },
  { id: "w3", name: "Keyword Agent", what: "adds the keywords and where they go", spot: [1110, SY1 + 30] },
  { id: "w4", name: "Content Agent", what: "writes the page, with an enquiry form", spot: [910, SY2 + 30] },
  { id: "w5", name: "Meta & SEO Agent", what: "adds meta tags and schema so AI can cite it", spot: [630, SY2 + 30] },
  { id: "bot", name: "Assembler", what: "builds the finished page", spot: [450, SY2 + 108] },
  { id: "w6", name: "QA Gate Agent", what: "scores the page; nothing weak ships", spot: [290, SY2 + 108] },
  { id: "you", name: "You", what: "approve the page, or let it publish itself", spot: [660, HUMAN[1]] },
  { id: "lead", name: "Lead Agent", what: "catches the enquiry and names the AI that sent it", spot: [490, LEAD[1]] },
];
const N = STEPS.length;
const stepOf = (id: string) => STEPS.findIndex((st) => st.id === id);
const WALKER_STEP = [4, 5, 6, 7, 8, 9, 11]; // step index where each walker works
const WALKER_HANDS_AT = [5, 6, 7, 8, 9, 10, 12]; // step where it walks the page to the next one
const START: Pt = [470, SY0 + 56]; // where the page guide walks in
const STATION_SEAT: Record<string, Pt> = { exec: EXEC.seat, monitor: MONITOR.seat, audit: SITE_AUDIT.seat, brand: BRAND.seat };

export default function AgentFloor() {
  const runRef = useRef<HTMLDivElement>(null);
  const pinRef = useRef<HTMLDivElement>(null);
  // 0..N: which step the scroll is on (whole part) and how far into it (fraction).
  const t = useScrollTime(
    () => {
      const run = runRef.current;
      const pin = pinRef.current;
      if (!run || !pin) return 0;
      return (STICKY_TOP - run.getBoundingClientRect().top) / Math.max(1, run.offsetHeight - pin.offsetHeight);
    },
    0,
    N - 0.001,
    N - 0.1,
  );
  const [clock, setClock] = useState("");

  useEffect(() => {
    const tick = () => setClock(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, []);

  const s = Math.min(N - 1, Math.floor(t));
  const f = t - s;
  const step = STEPS[s];
  const tt = t * 6; // blink and typing clock
  const glow = (i: number) => (i === s ? 1 : i < s ? 0.55 : 0.32); // active, done, waiting
  const engineIdx = Math.min(5, Math.floor(f * 6));
  const ex = 93 + engineIdx * 82;
  const walkerIdx = WALKER_STEP.indexOf(s);

  // The previous walker carries the page over at the start of each pipeline step.
  const walkers = WALKERS.map((w, i) => {
    const path = [w.seat, ...w.route];
    if (s === WALKER_HANDS_AT[i] && f < 0.7) {
      const len = pathLen(path);
      if (f < 0.3) return { at: along(path, f / 0.3), moving: true, walking: true, carrying: true, stride: ((f / 0.3) * len) / 7, bubble: `> page #1 to ${w.next}` };
      if (f < 0.4) return { at: path[path.length - 1], moving: true, walking: false, carrying: true, stride: 0, bubble: `> page #1 to ${w.next}` };
      const k = 1 - (f - 0.4) / 0.3;
      return { at: along(path, k), moving: true, walking: true, carrying: false, stride: (k * len) / 7, bubble: "" };
    }
    return { at: w.seat, moving: false, walking: false, carrying: false, stride: 0, bubble: "" };
  });
  const rowOf = (at: Pt) => (at[1] < 365 ? 1 : 2);

  const activeLine = (() => {
    switch (step.id) {
      case "exec":
        return `running · ${ENGINES[engineIdx].name}`;
      case "monitor":
        return MONITOR_LINES[Math.min(3, Math.floor(f * 4))];
      case "audit":
        return AUDIT_LINES[Math.min(2, Math.floor(f * 3))];
      case "brand":
        return BRAND_LINES[Math.min(2, Math.floor(f * 3))];
      case "bot":
        return f < 0.4 ? "ready for page #1" : "assembling page #1";
      case "w6":
        return f < 0.4 ? "page #1 arriving" : "scoring · 94/100 pass";
      case "you":
        return f < 0.4 ? "" : f < 0.75 ? "reviewing page #1" : "approved · publish";
      case "lead":
        return f < 0.4 ? "watching forms" : "alert sent · ChatGPT";
      default:
        return f < 0.4 ? "page #1 arriving" : `${WALKERS[walkerIdx].verb} · page #1`;
    }
  })();

  // Where the active agent sits (for its spotlight) and where its bubble goes.
  const anchor: Pt = (() => {
    if (walkerIdx >= 0) return WALKERS[walkerIdx].seat;
    if (STATION_SEAT[step.id]) return STATION_SEAT[step.id];
    if (step.id === "bot") return [BOT_X, SY2];
    return step.id === "you" ? HUMAN : LEAD;
  })();
  const standing = step.id === "you" || step.id === "lead";
  const bubbleAt: Pt = step.id === "bot" ? [BOT_X, SY2 - 62] : standing ? [anchor[0], anchor[1] - (step.id === "lead" ? 58 : 52)] : [anchor[0], anchor[1] - 44];

  // The guide walks from the last agent's spot to this one, then stands and explains.
  const from = s > 0 ? STEPS[s - 1].spot : START;
  const gk = Math.min(1, f / 0.35);
  const guideAt: Pt = [from[0] + (step.spot[0] - from[0]) * gk, from[1] + (step.spot[1] - from[1]) * gk];

  const live = s === N - 1; // the last step: page live and the enquiry arrives
  const enquiry = live && f >= 0.4;
  const beltK = step.id === "w6" && f < 0.35 ? f / 0.35 : -1;
  const holding = step.id === "you" && f >= 0.35;
  const W = `min(calc(100vw - 32px), calc((100vh - ${STICKY_TOP + 16}px) * 1.765))`;

  const seatedRep = (at: Pt, l: Look, seed: number, i: number) => (
    <g key={`r${seed}`} opacity={glow(i)}>
      <Rep at={[at[0], at[1] + 8]} l={l} t={tt} seed={seed} typing={i === s} />
    </g>
  );
  const desk = (x: number, sy: number, i: number, metal = false) => (
    <g key={`d${x}-${sy}`} opacity={glow(i)}>
      <Desk x={x} sy={sy} glow={i === s} metal={metal} />
    </g>
  );
  const walkerRep = (i: number) => {
    const ws = walkers[i];
    return (
      <g key={WALKERS[i].name} opacity={ws.moving ? 1 : glow(WALKER_STEP[i])}>
        <Rep at={ws.moving ? ws.at : [ws.at[0], ws.at[1] + 8]} l={WALKERS[i].look} t={tt} seed={i} walking={ws.walking} stride={ws.stride} carrying={ws.carrying} typing={!ws.moving && WALKER_STEP[i] === s} />
      </g>
    );
  };
  const beam = (d: string) => <path d={d} fill="none" stroke={ACCENT} strokeWidth={2} strokeDasharray="5 6" strokeDashoffset={-t * 300} opacity={0.75} />;

  return (
    // The spacer after the office is the scroll runway; the office stays pinned while it scrolls past.
    <div ref={runRef} className="mb-10 sm:mb-14" data-story-step="05" data-guide-in={START.join(",")} data-guide-out={STEPS[N - 1].spot.join(",")}>
      <div ref={pinRef} className="sticky" style={{ top: STICKY_TOP, width: W, marginLeft: `calc((100% - ${W}) / 2)` }}>
        <div className="pointer-events-none select-none bg-surface border border-hairline rounded-2xl p-1.5">
          <div className="overflow-x-auto rounded-xl [scrollbar-width:none] [&::-webkit-scrollbar]:hidden pointer-events-auto">
           <div className="relative min-w-[760px]">
            <svg
              viewBox={`0 0 ${VB_W} ${VB_H}`}
              className="block w-full min-w-[760px] h-auto pointer-events-none"
              role="img"
              aria-label="Animated office where a guide walks you through TopGEO's twelve AI agents one by one: they run your buyer questions on AI engines, track mentions, build a page on your website, and deliver the enquiry"
            >
              <Room />
              <ellipse cx={anchor[0]} cy={anchor[1] + (standing ? -10 : 14)} rx={105} ry={46} fill={ACCENT} opacity={0.09} />

              {/* the engine Execution is querying, and the data hand-offs of this step */}
              {step.id === "exec" && (
                <>
                  <rect x={56 + engineIdx * 82} y={32} width={74} height={52} rx={6} fill="none" stroke={ACCENT} strokeWidth={2.5} />
                  {beam(`M${ex} 82 L${ex} 86 L548 86 L548 124 L612 124`)}
                </>
              )}
              {step.id === "audit" && beam("M715 600 L690 600 L690 538 L325 538 L325 566")}
              {step.id === "w0" && f < 0.5 && beam("M820 178 L820 186 L290 186 L290 246 L218 246")}
              {step.id === "w4" && f < 0.5 && beam("M1115 615 L1140 615 L1140 452 L1062 452")}

              {/* desks by the AI-search wall */}
              {seatedRep(EXEC.seat, EXEC.look, 10, stepOf("exec"))}
              {seatedRep(MONITOR.seat, MONITOR.look, 11, stepOf("monitor"))}
              {desk(EXEC.seat[0], SY0, stepOf("exec"))}
              {desk(MONITOR.seat[0], SY0, stepOf("monitor"))}

              {/* top row: people, then their desks in front */}
              {walkers.map((ws, i) => (rowOf(ws.at) === 1 ? walkerRep(i) : null))}
              {[0, 1, 2, 3].map((i) => desk(WALKERS[i].seat[0], SY1, WALKER_STEP[i]))}

              {/* bottom row */}
              <g opacity={glow(stepOf("bot"))}>
                <Bot x={BOT_X} y={SY2 + 4} t={tt} busy={step.id === "bot" && f >= 0.4} />
              </g>
              {walkers.map((ws, i) => (rowOf(ws.at) === 2 ? walkerRep(i) : null))}
              {[4, 5, 6].map((i) => desk(WALKERS[i].seat[0], SY2, WALKER_STEP[i]))}
              {desk(BOT_X, SY2, stepOf("bot"), true)}
              {beltK >= 0 && (
                <g>
                  <rect x={366 - beltK * 120} y={SY2 - 17} width={14} height={11} rx={1.5} fill="var(--il-panel)" stroke="var(--il-mute)" />
                  <line x1={369 - beltK * 120} y1={SY2 - 13} x2={377 - beltK * 120} y2={SY2 - 13} stroke="var(--il-mute)" />
                </g>
              )}

              {/* desks below the bottom row */}
              {seatedRep(SITE_AUDIT.seat, SITE_AUDIT.look, 12, stepOf("audit"))}
              {seatedRep(BRAND.seat, BRAND.look, 13, stepOf("brand"))}
              {desk(SITE_AUDIT.seat[0], SYB, stepOf("audit"))}
              {desk(BRAND.seat[0], SYB, stepOf("brand"))}

              {/* website screen content */}
              <text x={148} y={603} fontFamily={MONO} fontSize={10} fontWeight={700} fill={TXT}>
                pages shipped · {live ? 1 : 0}
              </text>
              <text x={148} y={622} fontFamily={MONO} fontSize={10} fontWeight={700} fill={TXT}>
                enquiries · {enquiry ? 1 : 0}
              </text>
              <rect x={300} y={594} width={64} height={34} rx={4} fill="var(--il-tint)" stroke={enquiry ? ACCENT : "var(--il-tint-2)"} />
              <rect x={308} y={601} width={48} height={5} rx={2} fill="var(--il-tint-2)" />
              <rect x={308} y={610} width={48} height={5} rx={2} fill="var(--il-tint-2)" />
              <rect x={318} y={618} width={28} height={7} rx={2} fill={ACCENT} />

              {/* Lead Agent watches the forms; human in the loop: you approve before anything goes live */}
              <g opacity={glow(stepOf("lead"))}>
                <Rep at={LEAD} l={LEAD_LOOK} t={tt} seed={14} />
              </g>
              <g opacity={glow(stepOf("you"))}>
                <Rep at={HUMAN} l={HUMAN_LOOK} t={tt} seed={9} carrying={holding} />
              </g>
              {holding && f >= 0.75 && <circle cx={HUMAN[0] + 6} cy={HUMAN[1] - 24} r={4.5} fill="#22C55E" />}

              {/* the guide: same look and same paper as on the rest of the page */}
              <Rep at={guideAt} l={GUIDE_LOOK} t={tt} seed={20} walking={gk < 1} stride={t * 40} carrying />
              <GuideCarry x={guideAt[0]} y={guideAt[1]} stage={s < 8 ? 4 : 5} />

              {/* name tags */}
              {WALKERS.map((w, i) => (
                <g key={w.name} opacity={Math.max(0.6, glow(WALKER_STEP[i]))}>
                  <Tag x={w.seat[0]} y={w.seat[1] + 42} text={`${w.name} Agent`} />
                </g>
              ))}
              <g opacity={Math.max(0.6, glow(stepOf("bot")))}>
                <Tag x={BOT_X} y={SY2 + 42} text="Assembler" />
              </g>
              {STATIONS.map((st) => (
                <g key={st.name} opacity={Math.max(0.6, glow(STEPS.findIndex((x) => x.name === st.name)))}>
                  <Tag x={st.seat[0]} y={st.seat[1] + 13} text={st.name} />
                </g>
              ))}
              <Tag x={LEAD[0]} y={LEAD[1] + 6} text="Lead Agent" />
              <Tag x={HUMAN[0]} y={HUMAN[1] + 6} text="You · optional OK" />

              {/* speech: only the active agent, the hand-off, and the outcome */}
              {step.id === "exec" && <Bubble x={305} y={131} text={`buyer asks: ${QUESTIONS[0]}`} />}
              {activeLine && <Bubble x={bubbleAt[0]} y={bubbleAt[1]} text={activeLine} tone={(step.id === "lead" && enquiry) || (step.id === "you" && f >= 0.75) ? "accent" : "light"} />}
              {walkers.map((ws, i) => (ws.bubble ? <Bubble key={`b${i}`} x={ws.at[0]} y={ws.at[1] - 52} text={ws.bubble} /> : null))}
              {live && <Bubble x={205} y={562} text={enquiry ? "new enquiry · via ChatGPT" : "page #1 is live on your domain"} tone="accent" />}

            </svg>
            {/* the guide's board: what this step is (over the bookshelf corner, clear of every agent) */}
            <div className="absolute rounded-lg border border-hairline bg-surface-raised/95 shadow-sm px-[0.8%] py-[0.6%] text-left" style={{ left: "77%", top: "3.8%", width: "21%" }}>
              <div className="flex gap-[2px] mb-1" aria-hidden>
                {STEPS.map((st, n) => (
                  <span key={st.id} className={`h-[3px] flex-1 rounded-full ${n < s ? "bg-accent/40" : n === s ? "bg-accent" : "bg-ink/10"}`} />
                ))}
              </div>
              <p className="font-mono text-[clamp(8px,0.65vw,11px)] font-bold tracking-widest text-accent">
                STEP {s + 1}/{N}
              </p>
              <p className="font-display font-bold text-ink leading-tight text-[clamp(11px,1vw,16px)]">{step.name}</p>
              <p className="text-ink-muted leading-snug text-[clamp(10px,0.85vw,14px)]">{step.what}</p>
              <p className="mt-1 flex items-center gap-1 font-mono text-[clamp(7px,0.6vw,10px)] font-bold tracking-wider text-ink-faint uppercase">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#22C55E]" />
                {clock ? `${clock} · still working` : "always on · 24/7"}
              </p>
            </div>
           </div>
          </div>
        </div>
      </div>
      <div aria-hidden style={{ height: RUNWAY }} />
    </div>
  );
}
